<template>
  <div class="container-fluid py-4">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-10">
        <div v-if="loading" class="text-center">
          <font-awesome-icon icon="spinner" spin size="2x" class="text-primary"/>
          <p class="mt-2 text-muted">Loading campaign details...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
        </div>

        <div v-else-if="campaign">
          <!-- Campaign Header -->
          <div class="card mb-4">
            <div class="card-body text-center">
              <h2 class="mb-2">{{ campaign.title }}</h2>
              <p v-if="campaign.tagline" class="text-muted">{{ campaign.tagline }}</p>
            </div>
          </div>

          <div class="row">
            <!-- Collective Goals -->
            <div class="col-12 col-lg-6 mb-4" v-for="component in collectiveGoalComponents" :key="component.id">
              <div class="card h-100">
                <div class="card-body">
                  <csr-initiative
                    v-if="isCollectiveValid(component)"
                    :isPreview="false"
                    :isCampaign="true"
                    :monthlyTotalPoints="totalPoints"
                    :csrInitiative="component.collectiveGoal"
                  />
                  <collective-goal
                    v-else-if="isCollectiveGoalTemplateValid(component)"
                    :progress="getCollectiveGoalProgress(component)"
                    :template="component.collectiveGoal"
                  />
                </div>
              </div>
            </div>

            <!-- Leaderboard -->
            <div class="col-12 col-lg-6 mb-4" v-if="campaign.leaderboard">
              <div class="card h-100">
                <div class="card-body">
                  <h4 class="text-uppercase mb-3">{{ $t('campaign.titles.leaderboard') }}</h4>

                  <leaderboard-list
                    v-if="!campaign.table"
                    :rows="leaderboardRows"
                    :viewModalID="`leaderboard-details-${campaign.id}`"
                    :raffleMode="campaign.leaderboard.raffleMode"
                    :prizes="campaign.leaderboard.prizes"
                  />
                  <leaderboard-table-list
                    v-else
                    :rows="campaign.table.filter(el => el && el.user)"
                    :viewModalID="`leaderboard-details-${campaign.id}`"
                    :raffleMode="campaign.leaderboard.raffleMode"
                    :prizes="campaign.leaderboard.prizes"
                  />

                  <div class="d-flex flex-row justify-content-center align-items-center w-100 mt-3">
                    <b-btn
                      v-if="!campaign.table"
                      class="text-primary px-0"
                      size="sm"
                      v-b-modal="`leaderboard-details-${campaign.id}`"
                      variant="link"
                    >
                      {{ $t('campaign.buttons.view_all') }} ({{ leaderboardRows.length }})
                    </b-btn>
                    <b-btn
                      v-else
                      class="text-primary px-0"
                      size="sm"
                      v-b-modal="`leaderboard-details-${campaign.id}`"
                      variant="link"
                    >
                      {{ $t('campaign.buttons.view_all') }} ({{ (Array.isArray(campaign.table) ? campaign.table : []).filter(el => el && el.user).length }})
                    </b-btn>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Leaderboard Modal -->
    <b-modal
      v-if="campaign && campaign.leaderboard"
      :id="`leaderboard-details-${campaign.id}`"
      :title="$t('campaign.titles.leaderboard')"
      hide-footer
      lazy
      size="xl"
    >
      <leaderboard
        v-if="!campaign.table"
        :rows="leaderboardRows"
        :raffleMode="campaign.leaderboard.raffleMode"
        :prizes="campaign.leaderboard.prizes"
        :scores="campaign.leaderboard.scores"
        :campaignID="campaign.id"
      />
      <leaderboard-table
        v-else
        :rows="campaign.table.filter(el => el && el.user)"
        :raffleMode="campaign.leaderboard.raffleMode"
        :prizes="campaign.leaderboard.prizes"
        :scores="campaign.leaderboard.scores"
        :campaignID="campaign.id"
      />
    </b-modal>

  </div>
</template>

<script>
import gql from 'graphql-tag';
import _sortBy from 'lodash/sortBy';
import { mapRoot } from '@src/mixins/root';
import Leaderboard from '@src/components/referral-jam/Leaderboard';
import LeaderboardTable from '@src/components/campaign/Leaderboard';
import LeaderboardList from '@src/components/referral-jam/LeaderboardList';
import LeaderboardTableList from '@src/components/campaign/LeaderboardList';
import CsrInitiative from '@/components/dashboard/CsrInitiative';
import CollectiveGoal from '@/components/dashboard/collective-goal';

const GET_CAMPAIGN_QUERY = gql`
  query GetCampaignGamification($campaignID: ID!) {
    user {
      id
      campaign(where: { id: $campaignID }) {
        id
        title
        tagline

        users {
          id
          firstName
          lastName
          fullName
        }

        leaderboard {
          type
          raffleMode
          prizes {
            first {
              title
              description
              image {
                id
                fileLocation
              }
              url
              terms
            }
            second {
              title
              description
              image {
                id
                fileLocation
              }
              url
              terms
            }
            third {
              title
              description
              image {
                id
                fileLocation
              }
              url
              terms
            }
          }
          scores {
            id
            icon
            label
            eventNames
            maximum
            dailyMaximum
            weeklyMaximum
            value
            disabled
          }
          history
        }

        components {
          id
          collectiveGoal {
            description
            headline
            icon
            status
            goal
            conversion
            eventNames
            display {
              firstLine
              secondLine
              text
            }
            units {
              name
              conversion
              showWholeYearTotal
              display {
                firstLine
                secondLine
              }
              monthlyGoal {
                text
                totalSum
              }
            }
            scores {
              id
              icon
              label
              eventNames
              maximum
              dailyMaximum
              weeklyMaximum
              value
            }
          }
        }

        table {
          id
          user {
            id
            firstName
            lastName
            fullName
          }
          points
          stats
        }

        analytics {
          data
        }
      }
    }
  }
`;

export default {
  name: 'EngagementViewGamification',
  components: {
    Leaderboard,
    LeaderboardTable,
    LeaderboardList,
    LeaderboardTableList,
    CsrInitiative,
    CollectiveGoal,
  },
  props: {
    campaignID: {
      type: String,
      required: true,
    },
  },
  mixins: [
    mapRoot([ 'company', 'user' ])
  ],
  data() {
    return {
      error: null,
    };
  },
  apollo: {
    campaignData: {
      query: GET_CAMPAIGN_QUERY,
      variables() {
        return {
          campaignID: this.campaignID,
        };
      },
      update: ({ user }) => user,
      error(err) {
        console.error('Error loading campaign:', err);
        this.error = 'Failed to load campaign details';
      },
    },
  },
  computed: {
    loading() {
      return this.$apollo.queries.campaignData.loading;
    },
    campaign() {
      return this.campaignData && this.campaignData.campaign ? this.campaignData.campaign : null;
    },
    collectiveGoalComponents() {
      if (!this.campaign || !this.campaign.components) return [];
      return this.campaign.components.filter(el =>
        this.isCollectiveValid(el) || this.isCollectiveGoalTemplateValid(el)
      );
    },
    totalPoints() {
      const { leaderboard } = this.campaign || {};
      const { components } = this.campaign || {};
      const { collectiveGoal } = ((Array.isArray(components) ? components : []).find(el => this.isCollectiveValid(el)) || {});
      const { history, scores: leaderboardScores } = leaderboard || {};
      const { scores: collectiveGoalScores } = collectiveGoal || {};
      const scores = collectiveGoalScores || leaderboardScores || null;

      if (Array.isArray(scores) && scores.length && Array.isArray(history) && history.length) {
        return history.reduce((sum, el) => {
          if (!el.eventName) return 0;
          const { value } = scores.find(({ eventNames }) => Array.isArray(eventNames) && eventNames.includes(el.eventName)) || {};
          return sum + (value || 0);
        }, 0);
      }
      return 0;
    },
    leaderboardRows() {
      const { leaderboard, users } = this.campaign || {};
      if (!leaderboard || !users) return [];

      const { history, scores } = leaderboard;
      const rows = (Array.isArray(users) ? users : []).map(({ id, fullName: name }) => {
        const scoreIDs = {};
        const events = (Array.isArray(history) ? history : []).reduce((rollup, row) => {
          let scoreID = null;
          if (row && row.userID === id && row.eventName) {
            const { eventName, jobID, contentShareID } = row;

            if (jobID || contentShareID) {
              scoreID = jobID ? `JOB_${jobID}` : `CONTENT_${contentShareID}`;
              scoreIDs[scoreID] = (scoreIDs[scoreID] || 0) + 1;
            }

            const { maximum } = scores.find(({ eventNames }) => eventNames.includes(eventName)) || {};
            if (!scoreID || !maximum || (maximum && scoreID && scoreIDs[scoreID] <= maximum)) {
              rollup[eventName] = (rollup[eventName] || 0) + 1;
            }
          }
          return rollup;
        }, {});

        const total = Object.keys(events).reduce((t, eventName) => {
          const value = (Array.isArray(scores) ? scores : []).reduce((t, { eventNames, disabled, value }) => {
            if (Array.isArray(eventNames) && eventNames.includes(eventName) && !disabled && value > 0) {
              return t + value;
            } else {
              return t;
            }
          }, 0);
          return t + (value * (events[eventName] || 0));
        }, 0);

        return { id, name, ...events, total };
      });

      return _sortBy(rows, [ 'total' ]).reverse();
    },
  },
  methods: {
    isCollectiveValid(el) {
      return el.collectiveGoal &&
             el.collectiveGoal.description &&
             el.collectiveGoal.headline &&
             el.collectiveGoal.icon &&
             el.collectiveGoal.units &&
             el.collectiveGoal.status === 'ACTIVE';
    },
    isCollectiveGoalTemplateValid(el) {
      return el.collectiveGoal &&
             el.collectiveGoal.description &&
             el.collectiveGoal.headline &&
             el.collectiveGoal.icon &&
             el.collectiveGoal.goal;
    },
    getCollectiveGoalProgress(component) {
      const { analytics } = this.campaign || {};
      const { collectiveGoal } = component || {};
      const { data } = analytics || {};
      const { conversion, eventNames } = collectiveGoal || {};

      if (!data || !conversion || !Array.isArray(eventNames) || !eventNames.length) return 0;
      return Math.floor(eventNames.reduce((sum, eventName) => sum + (data[eventName] || 0), 0) / conversion);
    },
  },
};
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-body {
  padding: 2rem;
}
</style>



// WEBPACK FOOTER //
// src/views/engagement/ViewGamification.vue